import { useApp } from '@/contexts';

import { useCallback, useMemo, useRef, useState } from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';

import type { CronScheduleBuilderHandle } from '@/components/cron';
import {
  useGetAllTriggersQuery,
  useGetConfigTemplatesQuery,
} from '@/features/transformers/api/transformersApi';
import { useGetCurrentUserQuery } from '@/features/users/api/usersApi';
import type { CreateTriggerRequest, DeploymentOption, ScheduleType } from '@/types/schedule';
import { getApiErrorMessage } from '@/utils/apiErrors';

import { buildScheduleTriggerRequest } from '../utils/buildScheduleTriggerRequest';
import type { CronScheduleSaveState } from '../utils/cronScheduleSave';
import {
  DEFAULT_CRON_EXPRESSION,
  DEFAULT_DAILY_HOUR,
  DEFAULT_DAILY_MINUTE,
  DEFAULT_REPEATING_FREQUENCY,
  DEFAULT_REPEATING_FREQUENCY_UNIT,
  getDefaultCronTimeZone,
  getDefaultDailyTimeZone,
} from '../utils/scheduleDefaults';
import { isScheduleConfigValid } from '../utils/scheduleFormSchema';
import { DeploymentList } from './DeploymentSelector';
import { ScheduleConfigPanel } from './ScheduleConfigPanel';

interface CreateScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateTriggerRequest) => Promise<void>;
  deployments: DeploymentOption[];
  deploymentsLoading?: boolean;
  tenantId: string;
}

const STEPS = ['Select Recipe', 'Configure Schedule'];

interface FormState {
  deployment: DeploymentOption | null;
  scheduleType: ScheduleType;
  daily: {
    hour: number;
    minute: number;
    timeZone: string;
  };
  repeating: {
    frequency: number;
    frequencyUnit: 'minute' | 'hour';
    startingAt: { hour: number; minute: number } | null;
    timeZone: string;
  };
  cron: CronScheduleSaveState;
  templateName: string;
}

function createInitialFormState(): FormState {
  return {
    deployment: null,
    scheduleType: 'daily',
    daily: {
      hour: DEFAULT_DAILY_HOUR,
      minute: DEFAULT_DAILY_MINUTE,
      timeZone: getDefaultDailyTimeZone(),
    },
    repeating: {
      frequency: DEFAULT_REPEATING_FREQUENCY,
      frequencyUnit: DEFAULT_REPEATING_FREQUENCY_UNIT,
      startingAt: null,
      timeZone: getDefaultDailyTimeZone(),
    },
    cron: {
      expression: DEFAULT_CRON_EXPRESSION,
      explain: '',
      timeZone: getDefaultCronTimeZone(),
    },
    templateName: '',
  };
}

export const CreateScheduleDialog = ({
  open,
  onClose,
  onSubmit,
  deployments,
  deploymentsLoading = false,
}: CreateScheduleDialogProps) => {
  const { appId } = useApp();
  const cronBuilderRef = useRef<CronScheduleBuilderHandle>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [formState, setFormState] = useState<FormState>(createInitialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get current user to check if tech-support
  const { data: currentUser } = useGetCurrentUserQuery();
  const isTechSupport = currentUser?.role === 'tech-support';

  // Fetch all triggers to auto-populate template on deployment select (legacy behavior)
  const { data: allTriggers } = useGetAllTriggersQuery(appId || '', {
    skip: !appId,
  });

  // Fetch templates only for tech-support users
  const { data: templatesData } = useGetConfigTemplatesQuery(undefined, {
    skip: !isTechSupport,
  });

  // Extract and sort template names
  const availableTemplates = useMemo(() => {
    if (!templatesData || !Array.isArray(templatesData)) return [];
    return (templatesData as Array<{ Name?: string; name?: string }>)
      .map((t) => t.Name || t.name || '')
      .filter((name) => name.length > 0)
      .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }, [templatesData]);

  const handleClose = () => {
    setActiveStep(0);
    setFormState(createInitialFormState());
    setError(null);
    onClose();
  };

  // Prevent closing on backdrop click or escape key
  const handleDialogClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    handleClose();
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError(null);
  };

  const handleNext = () => {
    if (activeStep === 0 && !formState.deployment) {
      setError('Please select a deployment');
      return;
    }
    setError(null);
    setActiveStep((prev) => prev + 1);
  };

  const handleDeploymentSelect = (deployment: DeploymentOption) => {
    // Auto-populate template from existing trigger (legacy behavior)
    const { templateName: currentTemplate } = formState;
    const existingTrigger = allTriggers?.find(
      (t) =>
        String(t.environmentId) === String(deployment.environmentId) &&
        String(t.baseId) === String(deployment.baseId)
    );
    const templateName = existingTrigger?.templateName || currentTemplate;
    setFormState((prev) => ({ ...prev, deployment, templateName }));
    setError(null);
  };

  const handleScheduleTypeChange = (type: ScheduleType) => {
    setFormState((prev) => ({ ...prev, scheduleType: type }));
  };

  const handleDailyChange = (daily: FormState['daily']) => {
    setFormState((prev) => ({ ...prev, daily }));
  };

  const handleRepeatingChange = (repeating: FormState['repeating']) => {
    setFormState((prev) => ({ ...prev, repeating }));
  };

  const handleCronChange = (cron: FormState['cron']) => {
    setFormState((prev) => ({ ...prev, cron }));
  };

  const handleTemplateChange = (templateName: string) => {
    setFormState((prev) => ({ ...prev, templateName }));
  };

  const buildTriggerRequest = useCallback((): CreateTriggerRequest => {
    const { deployment, scheduleType, daily, repeating, cron, templateName } = formState;

    if (!deployment) {
      throw new Error('No deployment selected');
    }

    return buildScheduleTriggerRequest({
      mode: 'create',
      scheduleType,
      daily,
      repeating,
      cron,
      cronBuilderRef,
      baseIds: {
        baseid: parseInt(deployment.baseId, 10),
        environmentId: parseInt(deployment.environmentId, 10),
      },
      templateName: templateName || undefined,
    });
  }, [formState]);

  const handleSubmit = async () => {
    if (!isStepValid(1)) {
      setError('Please complete all required schedule fields before saving.');
      return;
    }

    if (formState.scheduleType === 'cron') {
      const built = cronBuilderRef.current?.buildForSave();
      if (!built?.isValid) {
        setError('Please fix the cron schedule before saving.');
        return;
      }
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const request = buildTriggerRequest();
      await onSubmit(request);

      handleClose();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to create schedule'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStepValid = (step: number): boolean => {
    if (step === 0) {
      return formState.deployment !== null;
    }
    if (step === 1) {
      return isScheduleConfigValid(
        formState.scheduleType,
        formState.daily,
        formState.repeating,
        formState.cron
      );
    }
    return true;
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth disableEscapeKeyDown>
      <DialogTitle>Create Schedule</DialogTitle>

      <DialogContent dividers>
        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Step Content */}
        <Box sx={{ minHeight: 300 }}>
          {/* Step 1: Select Deployment */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select a deployed recipe to schedule. Only recipes that have been deployed to an
                environment can be scheduled.
              </Typography>

              <DeploymentList
                deployments={deployments}
                selectedId={
                  formState.deployment
                    ? `${formState.deployment.baseId}-${formState.deployment.environmentId}`
                    : undefined
                }
                onSelect={handleDeploymentSelect}
                isLoading={deploymentsLoading}
              />
            </Box>
          )}

          {/* Step 2: Configure Schedule */}
          {activeStep === 1 && (
            <Box>
              {/* Selected Deployment Summary */}
              {formState.deployment && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  Scheduling: <strong>{formState.deployment.transformerName}</strong> in{' '}
                  <strong>{formState.deployment.environmentName}</strong>
                </Alert>
              )}

              {/* Template Selector (Tech-Support Only) */}
              {isTechSupport && availableTemplates.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Autocomplete
                    value={formState.templateName || null}
                    onChange={(_event, newValue) => handleTemplateChange(newValue || '')}
                    options={availableTemplates}
                    getOptionLabel={(option) => option.toUpperCase()}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Template (Optional)"
                        placeholder="Select a template..."
                        size="small"
                      />
                    )}
                    disabled={isSubmitting}
                    clearOnEscape
                    size="small"
                    sx={{ maxWidth: 300 }}
                  />
                </Box>
              )}

              {/* Schedule Type + Config */}
              <ScheduleConfigPanel
                scheduleType={formState.scheduleType}
                onScheduleTypeChange={handleScheduleTypeChange}
                daily={formState.daily}
                onDailyChange={handleDailyChange}
                repeating={formState.repeating}
                onRepeatingChange={handleRepeatingChange}
                cron={formState.cron}
                onCronChange={handleCronChange}
                cronBuilderRef={cronBuilderRef}
                showLegacyMigrationNotice={false}
                disabled={isSubmitting}
              />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="error" onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={isSubmitting}>
            Back
          </Button>
        )}
        {activeStep < STEPS.length - 1 ? (
          <Button variant="contained" onClick={handleNext} disabled={!isStepValid(activeStep)}>
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting || !isStepValid(activeStep)}
          >
            {isSubmitting ? 'Creating...' : 'Create Schedule'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
